import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { auth } from '../firebase/config';
import { debug } from '../lib/utils';
import { getDatabaseErrorTranslationKey } from '../lib/databaseErrors';

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const copyLinkToClipboard = (id: string) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    navigator.clipboard.writeText(url);
    // Optional: show a small tooltip or notification that the link was copied
  };

  const handleClearData = () => {
    setShowConfirmDialog(true);
  };

  const handleSuccessClose = () => {
    setShowSuccessDialog(false);
    window.location.reload();
  };

  const confirmClearData = async () => {
    try {
      setIsClearing(true);

      const { error } = await supabase.rpc('clear_my_data');
      if (error) {
        throw error;
      }

      localStorage.clear();
      sessionStorage.clear();

      // If user is logged in, quietly log them out
      if (currentUser) {
        await auth.signOut();
      }

      // Close confirm dialog and show success dialog
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);

    } catch (error) {
      debug('Error clearing data:', error);
      setShowConfirmDialog(false);
      // Still show the error in an alert for now
      alert(t(getDatabaseErrorTranslationKey(error, 'errorClearingData')));
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-12 px-4">
      <h1 className="text-3xl font-bold text-indigo-600 dark:text-indigo-400 mb-6">
        {t('privacyPolicy')}
      </h1>

      <div className="prose dark:prose-invert prose-indigo">
        <div className="group relative">
          <h2 id="privacy-policy" className="text-2xl font-semibold text-indigo-600 dark:text-indigo-400 mt-8 mb-4">
            {t('privacyPolicy')}
            <button
              onClick={() => copyLinkToClipboard('privacy-policy')}
              className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-indigo-500"
              title="Copy link to this section"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </button>
          </h2>
        </div>
        <div className="text-base">
          {t('privacyPolicyText').split('\n').map((paragraph, index) => (
            <p key={index} className="mb-3">{paragraph}</p>
          ))}
        </div>

        <div className="group relative">
          <h2 id="clear-data" className="text-2xl font-semibold text-indigo-600 dark:text-indigo-400 mt-8 mb-4">
            {t('clearData')}
            <button
              onClick={() => copyLinkToClipboard('clear-data')}
              className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-indigo-500"
              title="Copy link to this section"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </button>
          </h2>
        </div>
        <p className="text-base mb-4">{t('clearDataDescription')}</p>
        <button
          className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded"
          onClick={handleClearData}
          disabled={isClearing}
        >
          {isClearing ? t('clearing') : t('clearDataButton')}
        </button>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 max-w-sm w-full mx-4">
            <p className="text-gray-800 dark:text-gray-200 text-lg mb-4">
              {t('clearDataConfirm')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                disabled={isClearing}
              >
                {t('cancel')}
              </button>
              <button
                onClick={confirmClearData}
                className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center justify-center"
                disabled={isClearing}
              >
                {isClearing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('processing')}
                  </>
                ) : (
                  t('confirm')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Dialog */}
      {showSuccessDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 max-w-sm w-full mx-4">
            <div className="flex items-center justify-center mb-4 text-green-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 text-center mb-2">
              {t('success')}
            </h3>
            <p className="text-gray-700 dark:text-gray-300 text-center mb-6">
              {t('dataCleared')}
            </p>
            <div className="flex justify-center">
              <button
                onClick={handleSuccessClose}
                className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                {t('ok')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacyPolicy;
